import React from "react";
import { Modal, ModalHeader, ModalTitle, ModalBody, ModalFooter } from "@common/components/modal";
import { Button } from "@common/components/Button";
import { FormControl } from "@common/components/form-control";
import { Flex } from "@common/components/Flex";
import { trDts } from "@modules/datasheets/utils/i18n";
import { MessageBox } from "@common/components/MessageBox";
import { MessageProps } from "@common/components/MessageBox.types";

interface RenameFormatModalProps {
  show: boolean;
  currentFormatName: string;
  onClose: () => void;
  onConfirm: (newName: string) => void;
  messageProps?: MessageProps;
  closeMessage: () => void;
}

export const RenameFormatModal: React.FC<RenameFormatModalProps> = ({
  show,
  currentFormatName,
  onClose,
  onConfirm,
  messageProps,
  closeMessage,
}) => {
  const [newFormatName, setNewFormatName] = React.useState<string>(currentFormatName);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  React.useEffect(() => {
    if (show) {
      setNewFormatName(currentFormatName);
    }
  }, [show, currentFormatName]);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewFormatName(e.target.value);
  };

  const handleOK = async () => {
    const trimmedName = newFormatName.trim();

    if (!trimmedName) {
      return;
    }

    // Check if the new name matches the current name (case-insensitive)
    if (trimmedName.toLowerCase() === currentFormatName.trim().toLowerCase()) {
      onClose();
      return;
    }

    setIsSubmitting(true);
    
    try {
      await onConfirm(trimmedName);
      setIsSubmitting(false);
    } catch (err) {
      setIsSubmitting(false);
      throw err;
    }
  };

  const handleCancel = () => {
    setNewFormatName(currentFormatName);
    onClose();
  };

  const isNameValid = newFormatName.trim().length > 0;

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && isNameValid && !isSubmitting) {
      handleOK();
    }
  };

  return (
    <>
      {messageProps && messageProps.show && (
        <MessageBox hideMessageBox={closeMessage} {...messageProps} />
      )}
      <Modal
        show={show}
        backdrop="static"
        bsClass="bootstrap modal"
        animation={false}
        onEscapeKeyDown={handleCancel}
        onHide={handleCancel}
      >
        <ModalHeader closeButton onHide={handleCancel}>
          <ModalTitle>{trDts("Rename Datasheet Format")}</ModalTitle>
        </ModalHeader>
        <ModalBody>
          <Flex alignItems="center" style={{ marginBottom: 20 }}>
            <span style={{ minWidth: 120, marginRight: 8 }}>
              {trDts("Datasheet Name")}
            </span>
            <FormControl
              value={newFormatName}
              onChange={handleNameChange}
              onKeyDown={handleKeyDown}
              autoFocus
            />
          </Flex>
        </ModalBody>
        <ModalFooter>
          <Flex justify="flex-end" style={{ gap: 8 }}>
            <Button onClick={handleCancel} disabled={isSubmitting}>
              {trDts("Cancel")}
            </Button>
            <Button
              bsStyle="primary"
              onClick={handleOK}
              disabled={!isNameValid || isSubmitting}
            >
              {trDts("OK")}
            </Button>
          </Flex>
        </ModalFooter>
      </Modal>
    </>
  );
};
